import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Home, MapPin, Trash2, RotateCcw, Eye, EyeOff } from 'lucide-react';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import IdChip from '@/components/IdChip';
import { toast } from 'sonner';

export default function PropertiesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // Iter178 — soft-delete view modes. 'live' (default), 'all' (incl
  // soft-deleted), 'deleted' (only soft-deleted, with Restore CTA).
  const [viewMode, setViewMode] = useState('live');
  // Iter194 — Phase C status filter. 'active' (default — hides
  // shadowed + inactive), 'include_shadowed' (active + shadowed),
  // 'shadowed' / 'inactive' (only that bucket), 'all' (everything).
  const [statusFilter, setStatusFilter] = useState('active');
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const clientId = searchParams.get('client_id') || '';
    const flag = viewMode === 'all' ? '&include_deleted=true' : viewMode === 'deleted' ? '&only_deleted=true' : '';
    const { data } = await api.get(`/properties?page=${page}&search=${search}&client_id=${clientId}&status=${statusFilter}${flag}`);
    setProperties(data.items);
    setTotal(data.total);
    setLoading(false);
  }, [api, page, search, searchParams, viewMode, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const restoreProperty = async (p) => {
    try {
      await api.post(`/properties/${p._id}/restore`);
      toast.success('Property restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  return (
    <div data-testid="properties-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Properties</h1>
          <p className="text-sm text-slate-500 mt-1">{total} {viewMode === 'deleted' ? 'deleted ' : viewMode === 'all' ? 'total (incl deleted) ' : ''}properties</p>
        </div>
        {/* Iter178 — view-mode picker. */}
        <div data-testid="properties-view-mode" className="flex items-center gap-1 border border-[#E2E2DF] rounded-md p-0.5 bg-slate-50/50 text-xs">
          {[
            { k: 'live',    label: 'Live',           icon: <Eye size={11} /> },
            { k: 'all',     label: 'Incl. deleted',  icon: <EyeOff size={11} /> },
            { k: 'deleted', label: 'Deleted',        icon: <Trash2 size={11} /> },
          ].map(opt => (
            <button
              key={opt.k}
              data-testid={`properties-view-${opt.k}`}
              onClick={() => { setViewMode(opt.k); setPage(1); }}
              className={`flex items-center gap-1 px-2.5 py-1 rounded font-semibold transition ${viewMode === opt.k ? 'bg-white text-[#D35400] shadow-sm border border-[#D35400]/30' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by P# (e.g. P3073), street, suburb…" className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
      </div>
      {/* Iter194 — Phase C status filter pills */}
      <div data-testid="property-status-filter" className="flex items-center gap-1.5 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Status</span>
        {[
          { k: 'active',           label: 'Active' },
          { k: 'include_shadowed', label: 'Active + Shadowed' },
          { k: 'shadowed',         label: 'Shadowed only' },
          { k: 'inactive',         label: 'Inactive only' },
          { k: 'all',              label: 'All' },
        ].map(opt => (
          <button
            key={opt.k}
            data-testid={`property-status-filter-${opt.k}`}
            onClick={() => { setStatusFilter(opt.k); setPage(1); }}
            className={`px-2 py-1 rounded border ${statusFilter === opt.k ? 'border-[#D35400] bg-[#FBE9D8] text-[#D35400] font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >{opt.label}</button>
        ))}
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map(p => (
            <div
              key={p._id}
              data-testid={`property-card-${p._id}`}
              className={`relative bg-white border rounded-md p-4 hover:shadow-sm transition-all cursor-pointer group ${p.is_deleted ? 'border-rose-200 bg-rose-50/40' : 'border-[#E2E2DF] hover:border-slate-300'}`}
              onClick={() => navigate(`/properties/${p._id}`)}
            >
              {p.is_deleted && (
                <span data-testid={`property-deleted-badge-${p._id}`} className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-2 py-0.5">Deleted</span>
              )}
              {!p.is_deleted && p.status && p.status !== 'active' && (
                <span data-testid={`property-status-badge-${p._id}`} className={`absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.status === 'shadowed' ? 'text-amber-800 bg-amber-100 border border-amber-300' : 'text-rose-700 bg-rose-100 border border-rose-300'}`}>{p.status}</span>
              )}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#4A6FA5]/10 flex items-center justify-center flex-shrink-0"><Home size={18} className="text-[#4A6FA5]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{p.property_address_num} {p.property_address_street}</p>
                    {p.property_no && <IdChip type="property" value={p.property_no} size="md" testId={`property-list-chip-${p._id}`} />}
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11} />{p.property_address_suburb}, {p.property_address_city}</p>
                  {p.client_name && <p className="text-xs mt-1">Owner: <span data-testid={`client-link-${p._id}`} className="text-[#D35400] font-medium hover:underline cursor-pointer" onClick={e => { e.stopPropagation(); navigate(`/clients/${p.client_id}`); }}>{p.client_name}</span></p>}
                </div>
              </div>
              {/* Iter178 — per-row delete / restore actions. */}
              <div className="mt-3 flex items-center justify-end gap-1">
                {p.is_deleted ? (
                  <button
                    data-testid={`restore-property-${p._id}`}
                    onClick={(e) => { e.stopPropagation(); restoreProperty(p); }}
                    className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 border border-emerald-300 px-2 py-1 rounded hover:bg-emerald-50"
                  >
                    <RotateCcw size={11} /> Restore
                  </button>
                ) : (
                  <button
                    data-testid={`delete-property-card-${p._id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDelTarget({
                        kind: 'property',
                        id: p._id,
                        label: `${p.property_address_num || ''} ${p.property_address_street || ''}`.trim() || `Property ${p.property_no || p._id.slice(-6)}`,
                      });
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[11px] font-semibold text-rose-600 border border-rose-200 px-2 py-1 rounded hover:bg-rose-50"
                    title="Delete property (soft or hard)"
                  >
                    <Trash2 size={11} /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
          {properties.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No properties found</div>}
        </div>
      )}

      {delTarget && (
        <DeleteConfirmDialog
          kind={delTarget.kind}
          entityId={delTarget.id}
          label={delTarget.label}
          api={api}
          onClose={() => setDelTarget(null)}
          onDeleted={() => { setDelTarget(null); load(); }}
        />
      )}
    </div>
  );
}
